import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LedgerEntry } from '../../types/ledger';
import { formatDate } from '../../data/mockLedgerData';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';
import { useAuth } from '../../context/AuthContext';
import { getCurrencyFromPhone } from '../../data/countries';

interface LedgerEntryCardProps {
  entry: LedgerEntry;
  onPress: () => void;
}

const LedgerEntryCard: React.FC<LedgerEntryCardProps> = ({ entry, onPress }) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const currency = getCurrencyFromPhone(user?.phoneNumber);
  const isReceived = entry.type === 'received';
  
  return (
    <TouchableOpacity 
      style={[
        styles.card, 
        { 
          backgroundColor: theme.card, 
          borderColor: theme.border,
        }
      ]} 
      onPress={onPress} 
      activeOpacity={0.7}
    >
      {/* Type Tag at top right */}
      <View style={[
        styles.typeTag, 
        isReceived ? styles.receivedTag : styles.paidTag
      ]}>
        <ThemedText style={[styles.typeTagText, isReceived ? styles.receivedTagText : styles.paidTagText]}>
          {isReceived ? 'Received' : 'Paid'}
        </ThemedText>
      </View>
      
      <View style={styles.content}>
        <View style={styles.header}>
          <ThemedText style={[styles.personName, { color: theme.text }]} numberOfLines={1}>
            {entry.personName}
          </ThemedText>
        </View>
        
        <View style={styles.amountRow}>
          <ThemedText style={[styles.amount, isReceived ? styles.receivedAmount : styles.paidAmount]}>
            {isReceived ? '+' : '-'} {currency.symbol} {entry.amount.toLocaleString('en-IN')}
          </ThemedText>
        </View>
        
        <View style={styles.details}>
          <Ionicons 
            name="calendar-outline" 
            size={14} 
            color={theme.textSecondary} 
            style={styles.dateIcon}
          />
          <ThemedText style={[styles.date, { color: theme.textSecondary }]}>
            {formatDate(entry.date)}
          </ThemedText>
        </View>
        
        {entry.note && (
          <ThemedText style={[styles.note, { color: theme.textSecondary }]} numberOfLines={2}>
            {entry.note}
          </ThemedText>
        )}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    position: 'relative',
    padding: 16,
    paddingTop: 24,
    marginHorizontal: 16,
    marginBottom: 12,
    borderRadius: 12,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 3,
  },
  typeTag: {
    position: 'absolute',
    top: -1,
    right: 12,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    borderWidth: 1,
    borderTopWidth: 0,
  },
  receivedTag: {
    backgroundColor: '#E8F5E9',
    borderColor: '#0A8A4A',
  },
  paidTag: {
    backgroundColor: '#FFEBEE',
    borderColor: '#D32F2F',
  },
  typeTagText: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  receivedTagText: {
    color: '#0A8A4A',
  },
  paidTagText: {
    color: '#D32F2F',
  },
  content: {
    flex: 1,
  },
  header: {
    marginBottom: 8,
  },
  personName: {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: 0.2,
  },
  amountRow: {
    marginBottom: 8,
  },
  amount: {
    fontSize: 24,
    fontWeight: '800',
    letterSpacing: 0.3,
  },
  receivedAmount: {
    color: '#0A8A4A',
  },
  paidAmount: {
    color: '#D32F2F',
  },
  details: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  dateIcon: {
    marginRight: 4,
  },
  date: {
    fontSize: 13,
    fontWeight: '500',
  },
  note: {
    fontSize: 13,
    lineHeight: 18,
    marginTop: 6,
    fontStyle: 'italic',
  },
});

export default LedgerEntryCard;
